import { cn, fieldFocusWithinClasses, useTranslate } from '@athena/ui'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'

export interface MarkdownEditorHandle {
  /** Scrolls to a 1-based line, puts the caret on it, and takes focus. */
  focusLine: (line: number) => void
}

export interface MarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  /** 1-based lines the parser rejected. Flagged in the gutter. */
  errorLines?: readonly number[]
  label?: string
  id?: string
  describedBy?: string
}

const LINE_HEIGHT = 24

/**
 * Markdown source editor (handoff, Organisms > Markdown editor & preview):
 * dark surface, monospace, a gutter of line numbers with the failing lines
 * flagged.
 *
 * A textarea rather than a code editor component. The gutter is a second
 * column whose scroll position follows the textarea's, which is the whole
 * trick: line height is fixed so line N is always at the same offset in both.
 * A real editor would buy syntax highlighting, and the practice format has no
 * syntax worth highlighting.
 *
 * ── Why this is not the `Textarea` atom ──────────────────────────────────
 *
 * A deliberate exception, and the only native form control left in the app.
 * The atom is a field: a light box on `bg-surface`, `border-border-strong`,
 * the shared focus glow, a label above it and a message slot below. This is
 * none of those things — it is the left half of a two-column editor on the
 * app's one dark surface, and it has to give up the border, the radius and
 * the focus treatment to sit flush against a gutter that scrolls with it.
 *
 * Wrapping the atom would mean overriding every part of it that makes it the
 * atom, which is a worse outcome than not using it: the next person to change
 * the field treatment would have to discover that one caller needs it not to
 * apply. The tokens are still Daylight's — `text-body-sm`, `rounded-md` on
 * the frame, `bg-text`, `caret-accent` — so this is off the component, not
 * off the design system.
 */
export const MarkdownEditor = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(
  function MarkdownEditor({ value, onChange, errorLines = [], label, id, describedBy }, ref) {
    const t = useTranslate()
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const gutterRef = useRef<HTMLDivElement>(null)
    const [scrollTop, setScrollTop] = useState(0)

    const lines = value.split('\n')
    const flagged = new Set(errorLines)

    useImperativeHandle(ref, () => ({
      focusLine(line: number) {
        const textarea = textareaRef.current
        if (!textarea) return

        const offset = lines
          .slice(0, Math.max(0, line - 1))
          .reduce((sum, l) => sum + l.length + 1, 0)
        const end = offset + (lines[line - 1]?.length ?? 0)

        textarea.focus()
        textarea.setSelectionRange(offset, end)
        textarea.scrollTop = Math.max(0, (line - 1) * LINE_HEIGHT - textarea.clientHeight / 2)
        setScrollTop(textarea.scrollTop)
      },
    }))

    return (
      // The field's focus is drawn on this box, the way every other field's
      // is on its own border: the text area inside is flush to the gutter and
      // clipped by the rounded corners, and it had no focus state at all — a
      // tutor tabbing in saw a caret and nothing else.
      <div
        className={cn(
          'flex h-[28rem] overflow-hidden rounded-md border border-border bg-text',
          fieldFocusWithinClasses,
        )}
      >
        <div
          ref={gutterRef}
          aria-hidden="true"
          className="w-14 shrink-0 overflow-hidden border-r border-text-secondary/40 py-4 text-right font-mono text-body-sm"
        >
          <div style={{ transform: `translateY(${-scrollTop}px)` }}>
            {lines.map((_line, index) => {
              const lineNumber = index + 1
              return (
                <div
                  key={lineNumber}
                  style={{ height: LINE_HEIGHT }}
                  className={cn(
                    'px-3 leading-6 tabular-nums',
                    flagged.has(lineNumber)
                      ? 'bg-error/20 font-bold text-error'
                      : 'text-text-disabled',
                  )}
                >
                  {lineNumber}
                </div>
              )
            })}
          </div>
        </div>

        <textarea
          ref={textareaRef}
          id={id}
          aria-label={label ?? t('practice.editor.label')}
          aria-describedby={describedBy}
          spellCheck={false}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
          className={cn(
            'flex-1 resize-none bg-text px-4 py-4 font-mono text-body-sm leading-6',
            'text-text-inverse caret-accent outline-none',
            // text-disabled, where every field on a light surface uses
            // text-muted for its placeholder. Not an oversight and not a
            // candidate for the next consistency pass: this is the one dark
            // surface in the app, and the two tokens swap places on it.
            // #A1A1AA on #18181B is 6.91:1, while the #71717A the light
            // fields moved to would be 3.67:1 — applying that here would be
            // the regression, not the fix.
            'placeholder:text-text-disabled',
          )}
          // One question per type that needs showing, because the placeholder
          // is the only place a tutor meets `**Type:**` — the format's one
          // directive, and the only way to write an ordering or a matching
          // question. A line nobody has seen is a line nobody writes.
          //
          // Translated, with the directives left in English in both
          // dictionaries. They are not words, they are the format the parser
          // matches on (`packages/markdown/src/parser`), and a tutor who
          // copied a translated `**Respuesta:**` out of this box would get a
          // document the server rejects.
          placeholder={t('practice.editor.placeholder')}
        />
      </div>
    )
  },
)
